import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { AxiosResponse } from "axios";
import { useTranslation } from "react-i18next";
import type { Department } from "../../../contexts/AuthProvider";
import { useAnalytics } from "../../../contexts/analytics/hooks";
import { useHospitalData } from "../../../contexts/HospitalDataProvider";
import { useSnackbar } from "../../../contexts/SnackbarContext";
import type { TranslationKeys } from "../../../i18n/types";
import { backendService } from "../../../services/backend.service";
import type { WageRulesConfigResponse, WageSettingsResponse } from "../../../types/wageRules";
import type { BreakRulesConfig, HoursRateConfig, OvertimeConfig } from "../../../utils/models";
import { updateWageSettingsCache } from "./useWageSettings";

/** The `section` property on the `team_wage_settings_save_*` events. */
export type WageSettingsSection = "training_orientation" | "hours_rate" | "overtime" | "break_rules";

type WageRulesSaveSpec<TRow> = {
	section: Exclude<WageSettingsSection, "training_orientation">;
	request: (departmentId: string, configs: TRow[]) => Promise<AxiosResponse<WageRulesConfigResponse<TRow>>>;
	/** Writes the STORED rows into the department, in the shape every other reader of `chosenDepartment` expects. */
	writeBack: (department: Department, configs: TRow[]) => Department;
	/** Writes the STORED rows into the tab's own read (`useWageSettings`), which is what the section's baseline comes from. */
	writeCache: (settings: WageSettingsResponse, configs: TRow[]) => WageSettingsResponse;
	successKey: TranslationKeys;
	errorKey: TranslationKeys;
};

/**
 * DAT-905 · Saves one wage rule table from Team → Wage Settings. Shape of `useSaveTrainingOrientationWage`.
 *
 * Resolves to the full response: `recalcQueued` picks the "being recalculated" message (every change,
 * a rename included, queues one), and `configs` is what the server stored, which
 * the section re-seeds its draft from. No refetch: the success path writes the
 * stored rows into the tab's own read, which the section's baseline comes from, AND into
 * `chosenDepartment`, so every other screen reading the rules from there stays consistent.
 */
const useSaveWageRulesConfig = <TRow>({ section, request, writeBack, writeCache, successKey, errorKey }: WageRulesSaveSpec<TRow>) => {
	const { t } = useTranslation();
	const { chosenDepartment, setChosenDepartment, showErrorSnackbar } = useHospitalData();
	const { showSnackbar } = useSnackbar();
	const { track, trackError } = useAnalytics();
	const queryClient = useQueryClient();
	const departmentId = chosenDepartment.department_id;

	return useMutation<WageRulesConfigResponse<TRow>, Error, TRow[]>({
		mutationFn: async (configs) => {
			const response = await request(departmentId, configs);
			return response.data;
		},
		onMutate: (configs) => {
			track("team_wage_settings_save_start", { department_id: departmentId, section, row_count: configs.length });
		},
		onSuccess: ({ changed, recalcQueued, configs }) => {
			updateWageSettingsCache(queryClient, departmentId, (settings) => writeCache(settings, configs));
			setChosenDepartment((prev) => writeBack(prev, configs));
			track("team_wage_settings_save_success", { department_id: departmentId, section, changed, recalc_queued: recalcQueued });
			showSnackbar(t(recalcQueued ? "team.wageSettings.saveSuccessRecalculating" : successKey), "success");
		},
		onError: (error) => {
			trackError("team_wage_settings_save_error", { department_id: departmentId, section, error: error.message });
			// showErrorSnackbar unwraps and translates the backend's UIVisibleError key; this string is
			// only the fallback for a non-UIVisibleError failure.
			showErrorSnackbar(t(errorKey), {}, error);
		},
		throwOnError: false,
	});
};

export const useSaveHoursRateConfig = () =>
	useSaveWageRulesConfig<HoursRateConfig>({
		section: "hours_rate",
		request: backendService.setHoursRateConfig,
		writeBack: (department, hoursRateConfig) => ({ ...department, hoursRateConfig }),
		writeCache: (settings, hoursRateConfig) => ({ ...settings, hoursRateConfig }),
		successKey: "team.wageSettings.hoursRate.saveSuccess",
		errorKey: "team.wageSettings.hoursRate.saveError",
	});

export const useSaveOvertimeConfig = () =>
	useSaveWageRulesConfig<OvertimeConfig>({
		section: "overtime",
		request: backendService.setOvertimeConfig,
		writeBack: (department, overtimeConfig) => ({ ...department, overtimeConfig }),
		writeCache: (settings, overtimeConfig) => ({ ...settings, overtimeConfig }),
		successKey: "team.wageSettings.overtime.saveSuccess",
		errorKey: "team.wageSettings.overtime.saveError",
	});

export const useSaveBreakRulesConfig = () =>
	useSaveWageRulesConfig<BreakRulesConfig>({
		section: "break_rules",
		request: backendService.setBreakRulesConfig,
		writeBack: (department, breakRulesConfig) => ({ ...department, breakRulesConfig }),
		writeCache: (settings, breakRulesConfig) => ({ ...settings, breakRulesConfig }),
		successKey: "team.wageSettings.breakRules.saveSuccess",
		errorKey: "team.wageSettings.breakRules.saveError",
	});
